<template>
  <vs-popup class="churn-manager-popup" title="Manage Churn Reasons" :active.sync="active">
    <div class="p-6 flex flex-col gap-5">
      <div>
        <h3>Manage Churn Reasons</h3>
        <span class="text-base pt-3">Add, edit, or remove churn reasons</span>
      </div>
      <div class="flex flex-col lg:grid lg:grid-cols-3 gap-4">
        <div class="col-span-1 flex flex-col gap-2">
          <vs-input
            label="Reason Name"
            class="w-full"
            placeholder="e.g. Business Closure"
            v-model="currentReason.name"
            :success="!!(!errors.first('churn.reasonName') && currentReason.name)"
            val-icon-success="done"
            val-icon-danger="error"
            :danger="errors.first('churn.reasonName') ? true : false"
            v-validate="active ? 'required' : ''"
            name="reasonName"
            ref="reasonName"
            :danger-text="errors.first('churn.reasonName')"
            data-vv-as="reason name"
            data-vv-scope="churn"
          />
          <vs-button @click="addEditReason" class="mt-2">{{ isEdit ? 'Update' : 'Add' }} Reason</vs-button>
          <vs-button v-if="isEdit" @click="resetForm" type="border">Cancel</vs-button>
        </div>
        <vs-divider class="lg:hidden"></vs-divider>
        <div class="lg:col-span-2 lg:border lg:border-t-0 lg:border-r-0 lg:border-b-0 lg:border-solid d-theme-border-grey-light lg:pl-4">
          <h5 class="mb-3">Existing Churn Reasons</h5>
          <div v-if="reasons.length > 0" class="max-h-xs overflow-y-auto">
            <div v-for="(reason, index) in reasons.filter(r => r._id !== 'other')" :key="index">
              <div class="flex justify-between items-center">
                <span>{{ reason.name }}</span>
                <div class="flex justify-center">
                  <div v-if="currentlyDeletingId != reason._id" class="flex gap-2">
                    <vx-tooltip text="Edit" delay=".5s">
                      <vs-button @click="editReason(reason)" icon-pack="feather" icon="icon-edit" type="flat" />
                    </vx-tooltip>
                    <vx-tooltip text="Delete" delay=".5s">
                      <vs-button @click="deleteReason(reason)" icon-pack="feather" icon="icon-trash-2" type="flat" />
                    </vx-tooltip>
                  </div>
                  <div v-if="currentlyDeletingId === reason._id" class="flex gap-2" :key="'edit-' + reason._id">
                    <vx-tooltip text="Confirm Delete?" delay=".5s">
                      <vs-button @click="confirmDelete(reason)" icon-pack="feather" icon="icon-check" type="flat" color="danger" />
                    </vx-tooltip>
                    <vx-tooltip text="Cancel" delay=".5s">
                      <vs-button @click="currentlyDeletingId = ''" icon-pack="feather" icon="icon-x" type="flat" />
                    </vx-tooltip>
                  </div>
                </div>
              </div>
              <vs-divider class="my-1"></vs-divider>
            </div>
          </div>
          <div v-else class="flex items-center justify-center h-32">
            <p>No churn reasons.</p>
          </div>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ChurnReasonManager',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      reasons: [],
      active: false,
      currentReason: { name: '', id: '' },
      isEdit: false,
      currentlyDeletingId: '',
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.active = val;
        if (val) this.getReasons();
      },
    },
    active(val) {
      this.$emit('input', val);
    },
  },
  methods: {
    open() {
      this.active = true;
      this.getReasons();
    },
    close() {
      this.active = false;
    },
    ...mapActions('churnReasons', {
      fetchReasons: 'fetchReasons',
      createReason: 'createReason',
      updateReason: 'updateReason',
      deleteReasonAction: 'deleteReason',
    }),
    async getReasons() {
      try {
        const { data } = await this.fetchReasons();
        this.reasons = (data && data.data) ? data.data : [];
      } catch (err) {
        console.error('Error fetching churn reasons:', err);
        this.reasons = [];
      }
    },
    async addEditReason() {
      if (!this.currentReason.name || !this.currentReason.name.trim()) {
        this.errors.add({ field: 'reasonName', msg: 'Reason name is required', scope: 'churn' });
        return;
      }
      this.$vs.loading();
      
      try {
        if (!this.isEdit) {
          await this.createReason({ name: this.currentReason.name });
          this.$vs.notify({
            title: 'Success',
            text: 'Churn reason added successfully.',
            color: 'success',
          });
        } else {
          await this.updateReason({ _id: this.currentReason.id, name: this.currentReason.name });
          this.$vs.notify({
            title: 'Success',
            text: 'Churn reason updated successfully.',
            color: 'success',
          });
        }
        
        await this.getReasons();
        this.resetForm();
      } catch (err) {
        console.error('Error adding/editing churn reason:', err);
        this.$vs.notify({
          title: 'Failed',
          text: `Failed to ${this.isEdit ? 'update' : 'add'} churn reason. Try again!`,
          color: 'danger',
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    editReason(reason) {
      this.isEdit = true;
      this.currentReason.name = reason.name;
      this.currentReason.id = reason._id;
      this.currentlyDeletingId = '';
      this.$nextTick(() => {
        const inputRef = this.$refs.reasonName;
        if (inputRef && inputRef.$el) {
          const inputEl = inputRef.$el.querySelector('input');
          if (inputEl) inputEl.focus();
        }
      });
    },
    deleteReason(reason) {
      this.currentlyDeletingId = reason._id;
      this.isEdit = false;
      this.currentReason.name = '';
      this.currentReason.id = '';
      this.$validator.reset();
    },
    async confirmDelete(reason) {
      this.$vs.loading();
      
      try {
        await this.deleteReasonAction({ _id: reason._id });
        
        this.$vs.notify({
          title: 'Success',
          text: 'Churn reason deleted successfully.',
          color: 'success',
        });
        
        await this.getReasons();
        this.resetForm();
      } catch (err) {
        console.error('Error deleting churn reason:', err);
        this.$vs.notify({
          title: 'Failed',
          text: 'Failed to delete churn reason. Try again!',
          color: 'danger',
        });
      } finally {
        this.currentlyDeletingId = '';
        this.$vs.loading.close();
      }
    },
    resetForm() {
      this.isEdit = false;
      this.currentReason = { name: '', id: '' };
      this.currentlyDeletingId = '';
      this.$validator.reset();
    },
  },
};
</script>

<style scoped>
.churn-manager-popup {
  z-index: 9999;
}
</style>


